import { LoggerService } from "@bfx/logger";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { IndexType } from "./IndexType";
import { BfxColDef } from "./TableDefinition";
const logger = LoggerService.for(__dirname);

// real service imports
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

// Adapters
import { ColumnFilterType } from "./ColumnFilterType";

import { CellClassParams, ITooltipParams, RowNode } from "ag-grid-community";
import { ZERO } from "shared/base-components/number-spinner.component";
import {
  BmarkBaseRiskDisplayModel,
  BmarkRiskTGDisplayModel,
} from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
import {
  PARAM_TG_CD,
  PARAM_TG_ID,
  PARAM_TG_NAME,
} from "../../../app/bfx-bmark-summary/blotters/spot-risk/TgStatusColumnHeaderComponent";
import { TraderGroupPromiseLookupService } from "../lookup/model/impl/TraderGroupPromiseLookupService";
import { InstrumentColumnData } from "./benchmarks/BmarkSpotRisk.constants";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";
export const COLUMN_BMARK_INSTRUMENT: string = "instrument";
export const COLUMN_BMARK_TOTAL: string = "total";
export const COLUMN_BMARK_TG_PREFIX: string = "tg";
export const COLUMN_TG_ORDERS: string = "orderCount";

/**
 * Base generic blotter definition for all BM risks - defers to subclass to determine which
 * trader groups are included
 */

export abstract class BaseBMarkRiskTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected displayEmpty: boolean = false;

  protected myBmarkEnabled: boolean = false;
  protected bmarkTradingGroups: Array<CubiqTS.TradingGroupImpl>;

  constructor(
    profileService: ProfilePromiseActionService,
    protected _traderGroupLookup: TraderGroupLookupService,
    protected _traderGroupPrimaryLookup: TraderGroupPromiseLookupService,
  ) {
    super(profileService);
    this.defaultSortModel = [{ colId: "instrument", sort: "asc" }];

    this.profileService.getProfileConfig(this.getProfileSettingKey()).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });

    this.profileService
      .getProfileConfigUpdates(this.getProfileSettingKey())
      .subscribe((profileValue) => {
        this.mapUserSettings(profileValue);
      });

    if (this._traderGroupPrimaryLookup != null) {
      this._traderGroupPrimaryLookup.isPrimed().then((primed) => {
        if (primed) {
          this.createListOfBmarkTraderGroups();
        }
      });
    }
  }

  /**
   * Helper functions
   */
  protected mapUserSettings(profileValue: any) {
    if (profileValue) {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != null) {
        this.determineDefaultConfig(savedSettings);
      }
    } else {
      logger.out.warn(
        "BaseBmarkRiskTableDefinitionFactory --> mapUserSettings no profile value " + profileValue,
      );
    }
  }

  /**
   * Called when _traderGroupPrimaryLookup is primed to initialise trader groups
   */
  protected createListOfBmarkTraderGroups(): void {
    let allTradingGroups: Array<CubiqTS.TradingGroupImpl> = this._traderGroupLookup.getAll() ?? [];
    this.bmarkTradingGroups = allTradingGroups.filter((group) => this.isViewTradingGroup(group));
  }

  /**
   * Is the Trader Group a candidate for a total columnn in this view?
   * @param tradingGroup
   */
  protected abstract isViewTradingGroup(tradingGroup: CubiqTS.TradingGroupImpl): boolean;

  /**
   *  CReate generic column defn for Instrument - uses bmarkRiskInstrumentRenderer which can be set up
   * differently for spot and fwd
   * @returns
   */
  protected createInstrumentCol(): BfxColDef {
    const instrument: BfxColDef = this.initColdDef();
    instrument.headerName = "Instrument";
    instrument.headerTooltip = "Instrument";
    instrument.field = COLUMN_BMARK_INSTRUMENT;
    instrument.minWidth = this.fiveWs;
    instrument.width = this.sixWs;
    instrument.maxWidth = this.eightWs;
    instrument.columnFilterType = ColumnFilterType.NONE;
    instrument.omitFromPicker = true;
    instrument.disableInPicker = true;

    // Note: ws can't use cellClass here as that is only called if the actual cell value changes
    instrument.cellClassRules = {
      "blotter_spot-risk_instrument": () => true,
      positionWarning: (params: CellClassParams) => params.data?.isPositionWarning,
      positionAlert: (params: CellClassParams) => params.data?.isPositionAlert,
    };

    instrument.cellRenderer = "bmarkRiskInstrumentRenderer";
    instrument.valueGetter = (params) => {
      return new InstrumentColumnData(
        params.data.instrument,
        params.data.pricingError,
        params.data.isPositionWarning,
        params.data.isPositionAlert,
      );
    };
    instrument.showTooltip = false;
    if (!this.restoreFromConfig(instrument)) {
      instrument.position = this.position.next();
      logger.out.info("[" + instrument.position + `] : Added Instrument Column at next position`);
    } else {
      logger.out.info("[" + instrument.position + `] : Added Instrument Column from config`);
    }

    return instrument;
  }

  /**
   * Create overall Total column
   * @returns
   */
  protected createTotalCol(): BfxColDef {
    let total: BfxColDef = this.initColdDef();
    total.headerName = "Total";
    total.field = COLUMN_BMARK_TOTAL;
    total.indexType = IndexType.NaturalString;
    total.minWidth = this.sixWs;
    total.width = this.elevenWs;
    total.maxWidth = this.twentyWs;
    total.isPrimary = true; // adds 'blotter_primary_col' class
    total.cellClass = ["blotter_spot-risk_total"];
    total.columnFilterType = ColumnFilterType.INT;
    total.columnFilterData = null;
    total.valueGetter = function (params) {
      if (
        params != null &&
        params.data != null &&
        params.data[COLUMN_BMARK_TOTAL] instanceof CubiqTS.BigNumber
      ) {
        return params.data[COLUMN_BMARK_TOTAL].toNumber();
      } else {
        return 0;
      }
    }.bind(this);
    total.tooltipValueGetter = (params: ITooltipParams) => {
      const value: CubiqTS.BigNumber = params.data?.[COLUMN_BMARK_TOTAL] ?? ZERO;
      return value.toFormat(params.data?.fullPrecision ?? 0);
    };
    total.cellRenderer = "bmarkSpotRiskTotalsRenderer";
    total.cellRendererParams = {
      columnProperty: COLUMN_BMARK_TOTAL, ///'guinnessBlack'
      displayEmpty: this.displayEmpty,
    };

    if (!this.restoreFromConfig(total)) {
      total.position = this.position.next();
    }

    return total;
  }

  /**
   * Create list of columns for all supported trader groups
   * @param bmarkTradingGroups
   * @returns
   */
  protected createTraderGroupCols(bmarkTradingGroups: CubiqTS.TradingGroupImpl[]): BfxColDef[] {
    const retDefs = [];
    if (Array.isArray(bmarkTradingGroups) && bmarkTradingGroups.length > 0) {
      for (const tradingGroupDef of bmarkTradingGroups) {
        let tgColumn: BfxColDef = this.initColdDef();
        tgColumn.field = COLUMN_BMARK_TG_PREFIX + tradingGroupDef.groupId.toString();
        tgColumn.minWidth = this.sixWs;
        tgColumn.width = this.fourteenWs;
        tgColumn.maxWidth = this.twentyWs;
        tgColumn.valueGetter = function (params) {
          let tgData = params.data.tgData.find((item) =>
            (item.tgId as CubiqTS.Long).equals(tradingGroupDef.groupId),
          );
          if (tgData instanceof BmarkRiskTGDisplayModel) {
            return tgData.fillAmount + " of " + tgData.totalAmount.toNumber();
          } else {
            return 0;
          }
        }.bind(this);
        tgColumn.tooltipValueGetter = (params: ITooltipParams) => {
          const rowData = params.data as BmarkBaseRiskDisplayModel;
          const tgData = rowData?.tgData?.find(
            (item: BmarkRiskTGDisplayModel) =>
              item?.tgId instanceof CubiqTS.Long && item.tgId.equals(tradingGroupDef.groupId),
          );

          if (!(tgData instanceof BmarkRiskTGDisplayModel)) {
            return "";
          }
          const value = tgData.totalAmount;
          if (!(value instanceof CubiqTS.BigNumber)) {
            return "";
          }

          return value.toFormat(
            typeof rowData?.fullPrecision === "number" ? rowData.fullPrecision : 0,
          );
        };
        tgColumn.comparator = (
          _valueA: any,
          _valueB: any,
          nodeA: RowNode,
          nodeB: RowNode,
          _isInverted: boolean,
        ) => {
          let totalA = this.getTotal(tradingGroupDef.groupId, nodeA.data);
          let totalB = this.getTotal(tradingGroupDef.groupId, nodeB.data);

          return totalA - totalB;
        };
        tgColumn.columnFilterType = ColumnFilterType.TRADERGROUP;
        tgColumn.cellRenderer = "bmarkSpotRiskTraderGroupRenderer";
        tgColumn.cellRendererParams = {
          tgId: tradingGroupDef.groupId,
          orderCountProperty: COLUMN_TG_ORDERS,
          displayEmpty: this.displayEmpty,
        };

        tgColumn.headerName = tradingGroupDef.groupCode;
        // see BmarkSpotRiskBlotterComponent::frameworkComponents for registering of tgStatusColumnHeaderComponent
        tgColumn.headerComponent = tradingGroupDef.isExternallyFilledFg
          ? "tgStatusColumnHeaderComponent"
          : null;
        tgColumn.headerComponentParams = {
          [PARAM_TG_ID]: tradingGroupDef.groupId,
          [PARAM_TG_CD]: tradingGroupDef.groupCode,
          [PARAM_TG_NAME]: tradingGroupDef.typeDisplayName,
        };

        if (!this.restoreFromConfig(tgColumn)) {
          tgColumn.position = this.position.next();
        }

        retDefs.push(tgColumn);
      }
    }
    return retDefs;
  }

  /**
   * Lookup the trader group total from the data model
   * @param groupId
   * @param data
   * @returns
   */
  protected getTotal(groupId, data: any): number {
    let tgData = data.tgData.find((item) => (item.tgId as CubiqTS.Long).equals(groupId));

    if (tgData instanceof BmarkRiskTGDisplayModel) {
      return tgData.totalAmount.toNumber();
    }

    return 0;
  }

  protected initColdDef(): any {
    return {
      suppressMenu: true,
      cellClass: [DEFAULT_CELL_CLASS],
      hide: false,
    };
  }
}
